import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { joiResolver } from "@hookform/resolvers/joi";
import * as Joi from "joi";
import {
  IncrementalCardFormProps,
  INormalizedLmcDataType,
  lmcOptionComponentProps,
} from "./types";
import LmcUtil from "./util";
import LMCButtons from "../../../public/LMCButtons";
import { CustomInput } from "../../../atoms/CustomInput";

const createSchema = (t: any) =>
  Joi.object()
    .keys({
      comment: Joi.string()
        .max(100)
        .optional()
        .allow("", null)
        .label(t("dashboard.features.lmc.incrementalCard.validation.comment"))
        .messages({
          "string.max": t(
            "dashboard.features.lmc.incrementalCard.validation.commentMax"
          ),
        }),
    })
    .required();

const IncrementalCard = ({
  label,
  optionData,
  onSubmitCardData,
  playgroundDataHandler,
  handleCancelEdit,
  tutorial,
}: lmcOptionComponentProps) => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    getValues,
  } = useForm<IncrementalCardFormProps>({
    resolver: joiResolver(createSchema(t)),
    mode: "all",
  });

  useEffect(() => {
    if (optionData) {
      let denormalizedData = LmcUtil.unwrapper(
        "INCREMENTAL_CARD",
        optionData
      ) as IncrementalCardFormProps;

      reset({ ...denormalizedData });
    }
  }, [optionData, reset]);

  const onSubmit = (data: IncrementalCardFormProps) => {
    const normalizedData = LmcUtil.wrapper(
      "INCREMENTAL_CARD",
      data
    ) as INormalizedLmcDataType;

    onSubmitCardData("INCREMENTAL_CARD", normalizedData).then(() => {
      reset();
    });
  };

  useEffect(() => {
    playgroundDataHandler({
      type: "INCREMENTAL_CARD",
      data: LmcUtil.wrapper(
        "INCREMENTAL_CARD",
        getValues()
      ) as INormalizedLmcDataType,
    });

    watch((value, { name, type }) => {
      playgroundDataHandler({
        type: "INCREMENTAL_CARD",
        data: LmcUtil.wrapper(
          "INCREMENTAL_CARD",
          value
        ) as INormalizedLmcDataType,
      });
    });
  }, [getValues, playgroundDataHandler, watch]);

  return (
    <div className="lmc-card point-card w-full">
      <form onSubmit={handleSubmit(onSubmit)} className="w-full space-y-6">
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <div className="mb-5">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-3 mb-2">
              <span className="flex items-center justify-center w-7 h-7 bg-blue-500 text-white rounded-lg text-sm font-semibold shadow-sm">
                1
              </span>
              {t("dashboard.features.lmc.incrementalCard.sectionTitle")}
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              {t("dashboard.features.lmc.incrementalCard.sectionDesc")}
            </p>
          </div>

          <div className="space-y-4">
            <CustomInput
              rows={4}
              name="comment"
              control={control}
              error={errors.comment}
              helperText={errors.comment?.message}
              placeholder={
                t(
                  "dashboard.features.lmc.incrementalCard.placeholder1"
                ) as string
              }
              label={
                t("dashboard.features.lmc.incrementalCard.label1") as string
              }
            />
          </div>
        </div>

        <div className="pt-2">
          <LMCButtons tutorial={tutorial} handleCancelEdit={handleCancelEdit} />
        </div>
      </form>
    </div>
  );
};

export const IncrementalCardUi = ({
  data,
}: {
  data: INormalizedLmcDataType | null;
}) => {
  const { t } = useTranslation();

  return (
    <div className="self-stretch  flex-col justify-start items-center gap-6 flex">
      <div className="self-stretch  bg-gray-800 rounded-xl shadow flex-col justify-start items-center gap-6 flex">
        <div className="self-stretch px-4 pb-5 flex-col justify-center items-center gap-3 flex pt-4">
          <div className="text-center text-gray-400 text-base font-bold font-figtree leading-normal">
            {t("dashboard.features.lmc.incrementalCard.key3")}
          </div>

          <div className="self-stretch h-11 bg-gray-700 rounded-full flex-col justify-center items-center gap-2 flex">
            <div className="self-stretch grow shrink basis-0 px-6 py-2.5 justify-center items-center gap-2 inline-flex">
              <div className="text-center text-gray-200 text-base font-semibold font-figtree leading-normal">
                +1
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncrementalCard;
